import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
    Chip, Button, CircularProgress, Card, CardContent,
    Typography, Alert, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DataTable from '../../Generic/DataTable';
import ToolPassportModal from '../../Generic/ToolPassportModal';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useSnackbar } from 'notistack';

const MyTools = () => {
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const localizedTextsMap = useLocalizedTextsMap();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    // Use a dedicated menu number for MyTools
    useSetMenu(mainStore.MyToolsMenuType, mainStore.MyToolsMenuNo);

    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [tools, setTools] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);

    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingTool, setEditingTool] = useState(null);

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingTool, setDeletingTool] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');

    // View dialog state
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewingTool, setViewingTool] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    // Fetch user's approved tools
    const fetchTools = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolPassports/MyTools');
            setTools(response.data);
        } catch (error) {
            console.error('Error fetching tools:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        }
    }, [axiosPrivate, enqueueSnackbar, t]);

    // Fetch pending requests
    const fetchPendingRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolPassports/MyTools/PendingRequests');
            setPendingRequests(response.data);
        } catch (error) {
            console.error('Error fetching pending requests:', error);
        }
    }, [axiosPrivate]);

    // Fetch all data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([fetchTools(), fetchPendingRequests()]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [fetchTools, fetchPendingRequests]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Check if a tool has a pending request
    const hasPendingRequest = useCallback((toolId) => {
        return pendingRequests.some(r => r.originalToolPassportId === toolId);
    }, [pendingRequests]);

    // Get pending request type for a tool
    const getPendingRequestType = useCallback((toolId) => {
        const request = pendingRequests.find(r => r.originalToolPassportId === toolId);
        return request?.requestType;
    }, [pendingRequests]);

    // Handle view tool
    const handleView = (tool) => {
        setViewingTool(tool);
        setViewDialogOpen(true);
    };

    // Handle edit request
    const handleEdit = (tool) => {
        if (hasPendingRequest(tool.id)) {
            enqueueSnackbar(t('hasPendingRequest'), { variant: 'warning' });
            return;
        }
        navigate(`/MyTools/Edit/${tool.id}`);
    };

    // Handle delete request dialog open
    const handleDeleteClick = (tool) => {
        if (hasPendingRequest(tool.id)) {
            enqueueSnackbar(t('hasPendingRequest'), { variant: 'warning' });
            return;
        }
        setDeletingTool(tool);
        setDeleteReason('');
        setDeleteDialogOpen(true);
    };

    // Submit delete request
    const handleDeleteSubmit = async () => {
        if (!deletingTool) return;

        setSubmitting(true);
        try {
            await axiosPrivate.post(`/ToolPassports/MyTools/${deletingTool.id}/RequestDelete`, {
                reason: deleteReason
            });
            enqueueSnackbar(t('requestDeleteSuccess'), { variant: 'success' });
            setDeleteDialogOpen(false);
            setDeletingTool(null);
            setDeleteReason('');
            fetchData();
        } catch (error) {
            console.error('Error submitting delete request:', error);
            enqueueSnackbar(t('requestDeleteError'), { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Columns definition
    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: t('toolPassportName'),
            flex: 1,
            minWidth: 150,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'panelName',
            headerName: t('panel'),
            flex: 0.7,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'categoryName',
            headerName: t('category'),
            flex: 0.8,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'subcategoryName',
            headerName: t('subcategory'),
            flex: 0.8,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'typeOfToolName',
            headerName: t('typeOfTool'),
            flex: 0.7,
            minWidth: 100,
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
        },
        {
            field: 'pendingRequest',
            headerName: t('status'),
            flex: 0.6,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                const requestType = getPendingRequestType(params.row.id);
                if (requestType === 'Edit') {
                    return <Chip size="small" color="warning" icon={<PendingActionsIcon />} label={t('pendingEditRequest')} />;
                } else if (requestType === 'Delete') {
                    return <Chip size="small" color="error" icon={<PendingActionsIcon />} label={t('pendingDeleteRequest')} />;
                }
                return <Chip size="small" color="success" label={t('statusApproved')} />;
            },
        },
        {
            field: 'actions',
            headerName: t('action'),
            flex: 0.8,
            minWidth: 150,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'tableHeaderClassName',
            cellClassName: 'tableCellClassName',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                const isPending = hasPendingRequest(params.row.id);
                return (
                    <div className="d-flex justify-content-center gap-1">
                        <Tooltip title={t('viewTool')}>
                            <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleView(params.row)}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isPending ? t('hasPendingRequest') : t('requestEdit')}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEdit(params.row)}
                                    disabled={isPending}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={isPending ? t('hasPendingRequest') : t('requestDelete')}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(params.row)}
                                    disabled={isPending}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                );
            },
        },
    ], [t, hasPendingRequest, getPendingRequestType]);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">{t("myToolsTitle")}</h4>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/SubmitToolPassport')}
                    size="small"
                >
                    {t("submitNewTool")}
                </Button>
            </div>

            {/* Info Alert */}
            <Alert severity="info" className="mb-3">
                {t("myToolsInfo")}
            </Alert>

            {/* Pending Requests Alert */}
            {pendingRequests.length > 0 && (
                <Alert severity="warning" className="mb-3" icon={<PendingActionsIcon />}>
                    {t("pendingRequests")}: {pendingRequests.length}
                    <ul className="mb-0 mt-2">
                        {pendingRequests.map(req => (
                            <li key={req.id}>
                                <strong>{req.originalToolName}</strong> - {req.requestType === 'Edit' ? t('editRequest') : t('deleteRequest')}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-4 col-sm-6 mb-2">
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="success.main">
                                {tools.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('myTools')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-4 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="warning.main">
                                {pendingRequests.filter(r => r.requestType === 'Edit').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('pendingEditRequest')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-4 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="error.main">
                                {pendingRequests.filter(r => r.requestType === 'Delete').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('pendingDeleteRequest')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Data Table */}
            {loading ? (
                <div className="d-flex justify-content-center p-5">
                    <CircularProgress />
                </div>
            ) : tools.length === 0 ? (
                <div className="text-center p-5">
                    <BuildIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        {t('myToolsEmpty')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" className="mb-3">
                        {t('myToolsEmptyDescription')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/SubmitToolPassport')}
                    >
                        {t("submitNewTool")}
                    </Button>
                </div>
            ) : (
                <DataTable
                    rows={tools}
                    columns={columns}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    localeTextsMap={localizedTextsMap}
                />
            )}

            {/* View Tool Modal - Using the same component as admin */}
            <ToolPassportModal
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                toolPassport={viewingTool}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('confirmDeleteRequest')}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" className="mb-3">
                        {t('confirmDeleteRequestMessage', { name: deletingTool?.name })}
                    </Alert>
                    <Alert severity="info" className="mb-3">
                        {t('requestDeleteInfo')}
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={t('deleteReason')}
                        placeholder={t('deleteReasonPlaceholder')}
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteSubmit}
                        color="error"
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : t('requestDelete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default observer(MyTools);
